import { useEffect, useRef, type ReactNode } from "react";
import {
  ChevronDownIcon,
  MessageSquareIcon,
  PhoneIcon,
  SparklesIcon,
  TriangleAlertIcon,
  UserRoundIcon,
} from "lucide-react";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn, isUserTyping } from "@/lib/utils";
import {
  ReviewDecision,
  StevieRouteEligibility,
  StevieRouteMode,
} from "@/types";

export const ApplicationReviewModalActions = ({
  eligibilityForStevieOutreach,
  hasMessages = false,
  isAgentActive,
  isMessagingRailOpen = false,
  isShortlistDisabled,
  onAction,
  onRouteThroughStevie,
  onToggleMessagingRail,
  rejectControl,
  showCalendarWarning = false,
}: {
  // Which Stevie message the route would send; a BLOCKED verdict carries the
  // reason, shown on hover over the disabled option.
  eligibilityForStevieOutreach?: StevieRouteEligibility;
  hasMessages?: boolean;
  isAgentActive: boolean;
  isMessagingRailOpen?: boolean;
  isShortlistDisabled: boolean;
  // Shortlisting can be disabled for reasons other than a missing calendar
  // (mutation in flight, already shortlisted) — hosts opt into the warning.
  showCalendarWarning?: boolean;
  onAction: (decision: ReviewDecision) => void;
  // Replaces the plain Reject button (and its R shortcut) with a host-supplied
  // control — the V2 portal swaps in the reason-capturing reject dropdown.
  rejectControl?: ReactNode;
  // Omitted by hosts that don't offer the Stevie route, which drops the option.
  onRouteThroughStevie?: () => void;
  // Omitted where the host already shows messages (the v2 portal's comms rail),
  // which drops the toggle rather than offering a second way in.
  onToggleMessagingRail?: () => void;
}) => {
  const canRouteThroughStevie = !!onRouteThroughStevie;

  const rejectButtonRef = useRef<HTMLButtonElement>(null);
  const shortlistButtonRef = useRef<HTMLButtonElement>(null);

  // Stable refs so the keydown listener attaches once instead of re-binding
  // every time the parent's props change identity.
  const onActionRef = useRef(onAction);
  onActionRef.current = onAction;
  const isShortlistDisabledRef = useRef(isShortlistDisabled);
  isShortlistDisabledRef.current = isShortlistDisabled;
  const rejectControlRef = useRef(rejectControl);
  rejectControlRef.current = rejectControl;

  const flashPress = (button: HTMLButtonElement | null) => {
    if (!button) return;
    button.classList.add("scale-95");
    setTimeout(() => {
      button.classList.remove("scale-95");
    }, 150);
  };

  const handleRejectClick = () => {
    onAction(ReviewDecision.REJECT);
  };
  const handleShortlistClick = () => {
    onAction(ReviewDecision.SHORTLIST);
  };
  const handleManualShortlistClick = () => {
    onAction(ReviewDecision.SHORTLIST_MANUAL);
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isUserTyping()) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const key = event.key.toLowerCase();
      // A host-supplied reject control owns its own R handling. Checked against
      // undefined, not falsiness: a control still loading renders null, and
      // must not fall back to rejecting with no reason captured.
      if (key === "r" && rejectControlRef.current === undefined) {
        event.preventDefault();
        mixpanelUtils.trackKeyboardShortcutUsed(
          "R (Reject)",
          MIXPANEL_SCREENS.APPLICATION_REVIEW_PORTAL,
        );
        flashPress(rejectButtonRef.current);
        onActionRef.current(ReviewDecision.REJECT);
      } else if (key === "s") {
        event.preventDefault();
        if (isShortlistDisabledRef.current) return;
        mixpanelUtils.trackKeyboardShortcutUsed(
          "S (Shortlist)",
          MIXPANEL_SCREENS.APPLICATION_REVIEW_PORTAL,
        );
        flashPress(shortlistButtonRef.current);
        onActionRef.current(ReviewDecision.SHORTLIST);
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
    };
  }, []);

  // Nothing to choose between → plain shortlist button (no arrow).
  const renderShortlistControl = () => {
    if (!isAgentActive && !canRouteThroughStevie) {
      return (
        <Button
          ref={shortlistButtonRef}
          className="h-9 gap-1.5 rounded-full px-4 text-sm font-semibold shadow-sm transition-transform duration-150 active:scale-95"
          disabled={isShortlistDisabled}
          onClick={handleShortlistClick}
          size="sm"
          type="button"
        >
          Shortlist
          <kbd className="pointer-events-none ml-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded bg-primary-100 px-1 font-mono text-[10px] font-semibold text-primary-900">
            S
          </kbd>
        </Button>
      );
    }
    return (
      <Row className="overflow-hidden rounded-full shadow-sm">
        <Button
          ref={shortlistButtonRef}
          className="h-9 gap-1.5 rounded-none px-4 text-sm font-semibold transition-transform duration-150 active:scale-95"
          disabled={isShortlistDisabled}
          onClick={handleShortlistClick}
          size="sm"
          type="button"
        >
          Shortlist
          <kbd className="pointer-events-none ml-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded bg-primary-100 px-1 font-mono text-[10px] font-semibold text-primary-900">
            S
          </kbd>
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              className="h-9 w-8 rounded-none border-l border-white/25 px-0"
              size="sm"
              type="button"
            >
              <ChevronDownIcon className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72 p-1.5">
            <ShortlistDropdownOptions
              eligibilityForStevieOutreach={eligibilityForStevieOutreach}
              isAgentActive={isAgentActive}
              isShortlistDisabled={isShortlistDisabled}
              onManualShortlist={handleManualShortlistClick}
              onRouteThroughStevie={
                canRouteThroughStevie ? onRouteThroughStevie : undefined
              }
              onShortlist={handleShortlistClick}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      </Row>
    );
  };

  const render = () => (
    <Column className="items-center gap-2">
      <Row className="relative w-full items-center justify-center gap-2">
        {rejectControl !== undefined ? (
          rejectControl
        ) : (
          <Button
            ref={rejectButtonRef}
            className="h-9 gap-1.5 rounded-full border-rose-200 px-4 text-sm font-semibold text-rose-700 transition-transform duration-150 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-800 active:scale-95"
            onClick={handleRejectClick}
            size="sm"
            type="button"
            variant="outlineDestructive"
          >
            Reject
            <kbd className="pointer-events-none ml-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded bg-rose-100 px-1 font-mono text-[10px] font-semibold text-rose-800">
              R
            </kbd>
          </Button>
        )}
        {renderShortlistControl()}
        {onToggleMessagingRail ? (
          <Button
            className={cn(
              "absolute right-0 top-1/2 h-9 w-9 -translate-y-1/2 rounded-full",
              !hasMessages &&
                !isMessagingRailOpen &&
                "border-slate-200 text-slate-300 hover:text-slate-400",
            )}
            onClick={onToggleMessagingRail}
            onMouseDown={(event) => {
              // Prevent the rail's outside-click handler from closing the rail
              // before this click toggles it.
              event.stopPropagation();
            }}
            size="icon"
            type="button"
            variant={isMessagingRailOpen ? "default" : "outline"}
          >
            <div className="relative">
              <MessageSquareIcon className="h-4 w-4" />
              {hasMessages && (
                <span className="absolute -right-[4px] -top-px block h-[6px] w-[6px] rounded-full bg-red-500" />
              )}
            </div>
          </Button>
        ) : null}
      </Row>
      {showCalendarWarning ? (
        <Row className="items-center gap-1.5">
          <TriangleAlertIcon className="h-3.5 w-3.5 shrink-0 text-amber-500" />
          <Text className="text-xs font-medium text-amber-600">
            Set up your calendar to enable shortlisting
          </Text>
        </Row>
      ) : null}
    </Column>
  );

  return render();
};

// How the candidate gets handled after shortlisting, plus the Stevie route for
// candidacies a staffer isn't sure enough about to decide yet.
const ShortlistDropdownOptions = ({
  eligibilityForStevieOutreach,
  isAgentActive,
  isShortlistDisabled,
  onManualShortlist,
  onRouteThroughStevie,
  onShortlist,
}: {
  eligibilityForStevieOutreach?: StevieRouteEligibility;
  isAgentActive: boolean;
  isShortlistDisabled: boolean;
  onManualShortlist: () => void;
  onRouteThroughStevie?: () => void;
  onShortlist: () => void;
}) => {
  const isRenudge =
    eligibilityForStevieOutreach?.mode === StevieRouteMode.RENUDGE;
  const stevieIneligibleReason = eligibilityForStevieOutreach?.reason ?? null;

  return (
    <>
      {/* Agent paused → shortlisting never kicks off the agent, so there's no
          agent option to offer. Manual still shows: the Stevie route can open
          this menu on its own now. */}
      {isAgentActive ? (
        <DropdownMenuItem
          className="items-start gap-2.5 rounded-md py-2"
          disabled={isShortlistDisabled}
          onClick={onShortlist}
        >
          <Row className="mt-0.5 h-7 w-7 shrink-0 items-center justify-center rounded-md bg-purple-100">
            <SparklesIcon className="h-3.5 w-3.5 text-purple-600" />
          </Row>
          <Column className="gap-0.5">
            <Text className="text-sm font-medium text-slate-800">
              AI scheduling agent
            </Text>
            <Text className="text-xs font-normal leading-snug text-slate-500">
              Reaches out and schedules the candidate automatically.
            </Text>
          </Column>
        </DropdownMenuItem>
      ) : null}
      <DropdownMenuItem
        className="items-start gap-2.5 rounded-md py-2"
        onClick={onManualShortlist}
      >
        <Row className="mt-0.5 h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-100">
          <UserRoundIcon className="h-3.5 w-3.5 text-slate-600" />
        </Row>
        <Column className="gap-0.5">
          <Text className="text-sm font-medium text-slate-800">
            Manual mode
          </Text>
          <Text className="text-xs font-normal leading-snug text-slate-500">
            You handle the conversation yourself.
          </Text>
        </Column>
      </DropdownMenuItem>
      {/* A disabled menu item swallows pointer events, so the reason tooltip
          hangs off a wrapper around it. */}
      {onRouteThroughStevie ? (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Column>
                <DropdownMenuItem
                  className="items-start gap-2.5 rounded-md py-2"
                  disabled={stevieIneligibleReason !== null}
                  onClick={onRouteThroughStevie}
                >
                  <Row className="mt-0.5 h-7 w-7 shrink-0 items-center justify-center rounded-md bg-sky-100">
                    <PhoneIcon className="h-3.5 w-3.5 text-sky-600" />
                  </Row>
                  <Column className="gap-0.5">
                    <Text className="text-sm font-medium text-slate-800">
                      {isRenudge
                        ? "Nudge Stevie call"
                        : "Route through Stevie"}
                    </Text>
                    <Text className="text-xs font-normal leading-snug text-slate-500">
                      {isRenudge
                        ? "Nudge the candidate to take the Stevie call they were already texted about."
                        : "Have Stevie learn more info first."}
                    </Text>
                  </Column>
                </DropdownMenuItem>
              </Column>
            </TooltipTrigger>
            {stevieIneligibleReason !== null ? (
              <TooltipContent side="left">
                {stevieIneligibleReason}
              </TooltipContent>
            ) : null}
          </Tooltip>
        </TooltipProvider>
      ) : null}
    </>
  );
};
